import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';

// third-party
import { IntlProvider } from 'react-intl';

// project imports
import useLocale from 'hooks/useLocale';

const loadLocaleData = async (locale) => {
  // English is the canonical, complete message set. Load it as the BASE for
  // every locale and overlay the selected language on top. This guarantees any
  // key not yet translated falls back to its English string instead of leaking
  // the raw message id (e.g. "supervisor.todaysTeamSessions") into the UI —
  // react-intl's `defaultLocale` prop only affects date/number/plural
  // formatting, NOT message lookup, so the merge is what provides the fallback.
  const en = (await import('utils/locales/en.json')).default;
  if (locale === 'en') return en;

  let mod;
  switch (locale) {
    case 'hi':
      mod = await import('utils/locales/hi.json');
      break;
    case 'ta':
      mod = await import('utils/locales/ta.json');
      break;
    case 'ml':
      mod = await import('utils/locales/ml.json');
      break;
    case 'kn':
      mod = await import('utils/locales/kn.json');
      break;
    default:
      return en;
  }

  return { ...en, ...mod.default };
};

// ==============================|| LOCALIZATION ||============================== //

export default function Locales({ children }) {
  const { i18n } = useLocale();

  const [state, setState] = useState({ locale: i18n, messages: null });
  const loadingRef = useRef(null);

  useEffect(() => {
    const currentLocale = i18n;
    loadingRef.current = currentLocale;

    loadLocaleData(currentLocale).then((messages) => {
      if (loadingRef.current === currentLocale) {
        setState({ locale: currentLocale, messages });
      }
    });
  }, [i18n]);

  if (!state.messages) return null;

  return (
    <IntlProvider locale={state.locale} defaultLocale="en" messages={state.messages}>
      {children}
    </IntlProvider>
  );
}

Locales.propTypes = { children: PropTypes.node };
